import { setLocalItem, removeLocalItem } from '../common';
import { AppDispatch, RootState } from '../store';
import { resetAuthLoading, resetOtpState, saveToken, setAuthLoading, setOTPState } from './authActions';
import * as sdk from '../sdk';
import { isEmpty } from 'lodash';
import { getAccountTempHashId } from '../constants/mixpanel';
import { clearErrors, killLoading, storeSpecificErrors } from './commonActions';
import { isQAautomation, setCookie } from '../utils/helpers';
import { refreshAccount, setDarkMode } from './profileActions';
import { IAccount } from '../types/common';
import { removeIPQSCookies } from '../utils/ipQualityScore';
import { clearNotificationMessage } from './notificationActions';

export const storeJoinPreData = (payload: any) => {
  return {
    type: 'STORE_JOIN_PREDATA',
    payload,
  };
};

export const clearJoinPreData = () => {
  return {
    type: 'CLEAR_JOIN_PREDATA',
  };
};

export const verifyJoinEmail = (email: string): any => {
  return async (dispatch: AppDispatch) => {
    dispatch(setAuthLoading());
    dispatch(clearErrors());

    try {
      const data = { email, temp_hash_id: getAccountTempHashId() };

      await sdk.signupCheck(data);

      dispatch(storeJoinPreData({ ...data, isValidEmail: true, redirectTo: 'continue_email', status: 'success' }));
      dispatch(requestJoinOtpAction(email));
      dispatch(resetAuthLoading());
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: any) {
      let emailErroMessage = 'Please submit a valid email';

      // get email already in use message
      if (
        error?.response?.data?.email &&
        error?.response?.data?.email[0] &&
        error?.response?.data?.email[0] === 'This email is already in use.'
      ) {
        emailErroMessage = error?.response?.data?.email[0];
      }

      if (
        error?.response?.data?.code &&
        error?.response?.data?.code[0] &&
        error?.response?.data?.code[0] === '42221004'
      ) {
        emailErroMessage = 'This email is already in use.';
      }

      dispatch(
        storeJoinPreData({
          email,
          isValidEmail: false,
          redirectTo: 'continue_email',
          hasError: true,
          error: {
            errorCode: '',
            message: emailErroMessage,
          },
        })
      );
      dispatch(resetAuthLoading());
    }
  };
};

export const requestJoinOtpAction = (
  email: string,
  hCaptcha?: string,
  isResendOTP?: boolean,
  isResetOTP?: boolean
): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    dispatch(setAuthLoading());
    dispatch(clearErrors());
    dispatch(resetOtpState());

    try {
      const joinPreData = getState().auth?.joinPredata ?? {};

      const response = await sdk.requestEmailOtp(email, 'join', hCaptcha, isQAautomation(), isResendOTP, isResetOTP);

      dispatch(setOTPState());

      setTimeout(() => {
        dispatch(
          storeJoinPreData({
            email,
            isValidEmail: joinPreData?.isValidEmail ?? false,
            is_last_attempt: response?.data?.is_last_attempt ?? false,
            otpToken: response?.data?.token ?? null,
            redirectTo: 'continue_otp',
          })
        );
        dispatch(resetAuthLoading());
      }, 500);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      dispatch(resetAuthLoading());

      const joinPreData = getState().auth?.joinPredata ?? {};

      dispatch(
        storeJoinPreData({
          email,
          isValidEmail: joinPreData?.isValidEmail ?? false,
          redirectTo: 'continue_email',
          hasError: true,
        })
      );

      dispatch(storeSpecificErrors('banner', "Something wen't wrong, Please try again later."));
    }
  };
};

export const verifyJoinOtpAction = (otp: string, type: string): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    dispatch(setAuthLoading());
    dispatch(clearErrors());

    try {
      const joinPreData = getState().auth?.joinPredata ?? {};
      const response = await sdk.emailOtpVerify({ otp, type, token: joinPreData.otpToken, email: joinPreData.email });

      if (!response.data.is_new_user && !isEmpty(response.data.email_token)) {
        const formData = {
          login: joinPreData.email,
          email_token: response.data.email_token,
          type: 'join',
        };

        dispatch(passwordLessJoinLogin(formData));
        return;
      }

      if (isEmpty(response.data.email_token)) {
        dispatch(
          storeJoinPreData({
            email: joinPreData?.email ?? null,
            isValidEmail: joinPreData?.isValidEmail ?? false,
            otpToken: joinPreData?.otpToken ?? null,
            redirectTo: 'continue_otp',
            status: 'error',
          })
        );

        dispatch(resetAuthLoading());

        return;
      }

      const emailToken = response?.data?.email_token ?? null;
      const email = joinPreData?.email ?? null;

      dispatch(
        storeJoinPreData({
          email: email,
          isValidEmail: joinPreData?.isValidEmail ?? false,
          emailToken: emailToken,
          status: 'success',
          redirectTo: 'continue_otp',
        })
      );

      setLocalItem('join_predata', JSON.stringify({ email: email, emailToken: emailToken }));

      dispatch(resetAuthLoading());
    } catch (error: any) {
      dispatch(resetAuthLoading());

      const joinPreData = getState().auth?.joinPredata ?? {};
      const errorCode = error?.response?.data?.errors && error?.response?.data?.errors[0].code;

      if (errorCode === 40022005) {
        dispatch(
          storeJoinPreData({
            email: joinPreData?.email ?? null,
            isValidEmail: joinPreData?.isValidEmail ?? false,
            redirectTo: 'continue_otp',
            hasError: true,
            maxAttamptReached: errorCode === 40022005,
          })
        );
        return;
      }

      dispatch(storeSpecificErrors('banner', "Something wen't wrong, Please try again later."));
    }
  };
};

//passwordless - if email is already exist during signup, login to that user and redirect to dashboard
export const passwordLessJoinLogin = (formData: {
  login: string;
  email_token?: string;
  password?: string;
  'h-captcha-response'?: string;
  type: string;
}): any => {
  return async (dispatch: AppDispatch) => {
    try {
      const authResponse = await sdk.getFirstAuthToken(formData);

      if (authResponse.data && authResponse.status === 200) {
        // save token
        dispatch(saveToken(authResponse.data));
        // retrieve logged in profile.
        dispatch(refreshAccount())
          .then((result: IAccount) => {
            const darkmodeConfig = result?.account_config?.darkmode;

            dispatch(setDarkMode(darkmodeConfig));

            removeIPQSCookies();
            dispatch(clearErrors());
            setCookie('isAuthenticated', true, true);
            dispatch(clearNotificationMessage());
            dispatch(resetAuthLoading());

            setTimeout(() => {
              window.location.href = '/dashboard';
              dispatch(storeJoinPreData(null));
              removeLocalItem('join_predata');
            }, 2000);
          })
          .catch(() => {
            dispatch(resetAuthLoading());

            dispatch(
              storeJoinPreData({
                email: formData.login,
                redirectTo: 'create_account',
              })
            );

            dispatch(storeSpecificErrors('banner', "Something wen't wrong, Please try again later."));
          });

        dispatch(killLoading());
      } else {
        dispatch(resetAuthLoading());
        dispatch(storeJoinPreData({ email: formData.login, redirectTo: 'create_account' }));
        dispatch(storeSpecificErrors('banner', "Something wen't wrong, Please try again later."));
      }
    } catch (error: any) {
      dispatch(resetAuthLoading());
      const errorCode = error?.response?.data?.errors && error?.response?.data?.errors[0].code;

      if ([40024001].includes(errorCode)) {
        dispatch(storeSpecificErrors('banner', 'Invalid Captcha'));

        return;
      }

      if ([40101014, 40101015].includes(errorCode)) {
        //fraud deleted and suspended redirect to fraud delete and suspend error handling screen
        dispatch(
          storeJoinPreData({
            email: formData.login,
            redirectTo: 'create_account',
          })
        );

        dispatch(storeSpecificErrors('banner', "Something wen't wrong, Please try again later."));

        return;
      }

      dispatch(storeSpecificErrors('banner', "Something wen't wrong, Please try again later."));
    }
  };
};
